// 💬 COMPONENTE DE CHAT - DISCUSSÃO DE CASOS
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/components/ChatDiscussaoCaso.tsx

import React, { useState, useRef, useEffect } from "react";
import { Lightbulb, MessageCircle, Clock, Send, Trash2 } from "lucide-react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  writeBatch,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { callAnalyze } from "../../../lib/analyzeClient";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { coletarDadosCompletos } from "../services/coletarDadosPaciente";
// B284 (AI_123) — acesso AMPLO do chat aos dados clínicos: reusa os MESMOS
// formatadores validados da análise principal (em vez do resumo parcial que só
// expunha AFP + 3 labs). Todos recebem os dados já ANONIMIZADOS do coletor.
import {
  formatarContextoClinico,
  formatarScores,
  formatarMarcadores,
  formatarSorologias,
  formatarAvaliacaoClinica,
  formatarTendencias,
  formatarDadosTransplante,
  formatarInscricaoTx,
  formatarPrescricoes,
} from "../services/promptsMedicos";
import { obterContextoGuidelines } from "../rag";
import {
  MODOS_CHAT,
  getModoConfig,
  ModoChat,
} from "../services/modosChat";
import { useAuth } from "../../auth/context/AuthContext";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import { valoresLabParaIA } from "../services/valoresLabParaIA";

interface Mensagem {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ChatDiscussaoCasoProps {
  paciente: Paciente;
  onClose: () => void;
}

export function ChatDiscussaoCaso({
  paciente,
  onClose,
}: ChatDiscussaoCasoProps) {
  const { usuarioLogado } = useAuth();
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [inputMensagem, setInputMensagem] = useState("");
  const [loading, setLoading] = useState(false);
  const [dadosPaciente, setDadosPaciente] = useState<any>(null);
  const [carregandoDados, setCarregandoDados] = useState(true);
  // B330 (AI_148): modo do chat — muda só a postura da IA (persona + foco),
  // preservando os dados e as 16 instruções obrigatórias de segurança.
  const [modo, setModo] = useState<ModoChat>("discussao");
  // B330-FU: tooltip explicativo ao passar o cursor num modo (id + posição x).
  const [modoHover, setModoHover] = useState<{ id: ModoChat; left: number } | null>(
    null
  );
  const modosRowRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Referência da coleção de mensagens no Firestore
  const mensagensRef = collection(
    db,
    "chat-discussao",
    paciente.id,
    "mensagens"
  );

  // Salvar uma mensagem no Firestore
  const salvarMensagem = async (mensagem: Mensagem) => {
    try {
      await addDoc(mensagensRef, {
        role: mensagem.role,
        content: mensagem.content,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.warn("⚠️ Erro ao salvar mensagem no chat:", error);
    }
  };

  // Limpar todo o histórico do chat
  const limparHistorico = async () => {
    if (!window.confirm("Limpar todo o histórico de discussão deste paciente?"))
      return;
    try {
      const snapshot = await getDocs(mensagensRef);
      if (snapshot.empty) {
        setMensagens([]);
        return;
      }
      const batch = writeBatch(db);
      snapshot.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      setMensagens([]);
      console.log("✅ Histórico do chat limpo");
    } catch (error) {
      console.error("❌ Erro ao limpar histórico:", error);
    }
  };

  // Carregar dados do paciente e histórico do chat ao abrir
  useEffect(() => {
    const carregarDados = async () => {
      setCarregandoDados(true);
      try {
        const dados = await coletarDadosCompletos(paciente);
        setDadosPaciente(dados);

        // Tentar carregar histórico salvo
        const q = query(mensagensRef, orderBy("timestamp", "asc"));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          // Histórico existe — carregar mensagens salvas
          const mensagensSalvas: Mensagem[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              role: data.role,
              content: data.content,
              timestamp: data.timestamp?.toDate?.() || new Date(),
            };
          });
          setMensagens(mensagensSalvas);
          console.log(`✅ Chat carregado: ${mensagensSalvas.length} mensagens`);
        } else {
          // Sem histórico — criar mensagem inicial
          const mensagemInicial: Mensagem = {
            id: "inicial",
            role: "assistant",
            content: `Olá! Estou pronto para discutir o caso de **${
              paciente.nome
            }**, ${dados.idade} anos.

📋 **Dados disponíveis:**
- ${dados.consultas?.length || 0} consulta(s)
- ${dados.examesLab?.length || 0} exame(s) laboratorial(is)
- ${dados.marcadores?.length || 0} marcador(es) tumoral(is)
- ${dados.sorologias?.length || 0} sorologia(s)
- ${dados.imagens?.length || 0} exame(s) de imagem

Como posso ajudar? Você pode me perguntar sobre:
- Opções terapêuticas e condutas
- Interpretação de exames
- Critérios de transplante
- Prognóstico e estratificação de risco
- Comparação com guidelines (AASLD, EASL, ILTS)`,
            timestamp: new Date(),
          };
          setMensagens([mensagemInicial]);
          // Salvar mensagem inicial no Firestore
          await salvarMensagem(mensagemInicial);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
      setCarregandoDados(false);
    };

    carregarDados();
  }, [paciente]);

  // Scroll automático para última mensagem
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [mensagens]);

  // Gerar prompt do sistema
  const gerarPromptSistema = (dados: any, modoAtivo: ModoChat) => {
    // B284 (AI_123): debug console.log removido — vazava lesoesHepaticas/achados
    // de imagem (conteúdo clínico) para o console (mesma higiene LGPD do B196).
    // B330 (AI_148): persona e foco vêm do modo escolhido; travas inalteradas.
    const cfgModo = getModoConfig(modoAtivo);

    // Formatar exames de imagem
    const examesImagemFormatados =
      dados.imagens && dados.imagens.length > 0
        ? dados.imagens
            .map(
              (img: any) =>
                `### ${img.tipo || "Exame"} - ${
                  img.dataExame || img.data || "data não informada"
                }
${img.descricao ? `Descrição: ${img.descricao}` : ""}
${
  img.achados
    ? `- Lesões: ${img.achados.lesoesHepaticas || "Não descrito"}
- Status Milão: ${img.achados.statusMilao || "N/A"}
- Trombose portal: ${img.achados.trombosePortal || "N/A"}
- Conclusão: ${img.achados.conclusao || "Ver análise completa"}`
    : "⚠️ Exame não analisado pela IA"
}`
            )
            .join("\n---\n")
        : "Nenhum exame de imagem registrado";

    // Achados consolidados
    const achadosConsolidados =
      dados.achadosImagem && dados.achadosImagem.length > 0
        ? `- Lesões hepáticas: ${
            dados.achadosImagem[0]?.lesoesHepaticas || "N/A"
          }
- Status Milão: ${dados.achadosImagem[0]?.statusMilao || "N/A"}
- Trombose portal: ${dados.achadosImagem[0]?.trombosePortal || "N/A"}
- Ascite: ${dados.achadosImagem[0]?.ascite || "N/A"}
- Conclusão: ${dados.achadosImagem[0]?.conclusao || "N/A"}`
        : "Nenhum achado estruturado disponível";

    return `${cfgModo.persona}

## DADOS CLÍNICOS DO PACIENTE:
${formatarContextoClinico(dados)}

${formatarScores(dados)}

${formatarAvaliacaoClinica(dados)}

${formatarMarcadores(dados)}

${formatarSorologias(dados)}

${formatarTendencias(dados)}

${formatarDadosTransplante(dados)}
${formatarInscricaoTx(dados)}
${formatarPrescricoes(dados)}

## CONSULTAS RECENTES (10 mais recentes — ordem cronológica decrescente):
${
  dados.consultas
    ?.slice(0, 10)
    .map((c: any) => {
      const linhas: string[] = [`### ${c.data || "Data não registrada"}`];
      if (c.queixaPrincipal)
        linhas.push(`**Queixa principal:** ${c.queixaPrincipal}`);
      if (c.hma) linhas.push(`**História da doença atual:** ${c.hma}`);
      const sinaisVitais: string[] = [];
      if (c.peso) sinaisVitais.push(`Peso ${c.peso}kg`);
      if (c.altura) sinaisVitais.push(`Altura ${c.altura}cm`);
      if (c.pa) sinaisVitais.push(`PA ${c.pa}`);
      if (c.fc) sinaisVitais.push(`FC ${c.fc}`);
      if (sinaisVitais.length > 0)
        linhas.push(`**Sinais vitais:** ${sinaisVitais.join(" | ")}`);
      if (c.exameFisico) linhas.push(`**Exame físico:** ${c.exameFisico}`);
      if (c.revisaoSistemas)
        linhas.push(`**Revisão de sistemas:** ${c.revisaoSistemas}`);
      if (c.hipoteses)
        linhas.push(`**Hipóteses diagnósticas:** ${c.hipoteses}`);
      if (c.conduta) linhas.push(`**Conduta:** ${c.conduta}`);
      if (c.observacoes) linhas.push(`**Observações:** ${c.observacoes}`);
      return linhas.join("\n");
    })
    .join("\n\n---\n\n") || "Nenhuma consulta registrada"
}

## EXAMES LABORATORIAIS RECENTES (5 mais recentes):
${
  dados.examesLab && dados.examesLab.length > 0
    ? dados.examesLab
        .slice(-5)
        .map((e: any) => `- ${e.data}: ${valoresLabParaIA(e.valores)}`)
        .join("\n")
    : "Nenhum exame laboratorial disponível"
}

## EXAMES DE IMAGEM REALIZADOS (${dados.imagens?.length || 0} exames):
${examesImagemFormatados}

**⚠️ REGRA IMPORTANTE:** NÃO sugira exames de imagem que já foram realizados (listados acima). Se precisar de mais informações, solicite REVISÃO dos exames existentes.

## ACHADOS CONSOLIDADOS (mais recente):
${achadosConsolidados}

## SEU FOCO NESTA CONVERSA (modo selecionado pelo médico):
${cfgModo.foco}

## INSTRUÇÕES OBRIGATÓRIAS:
0. **FIDELIDADE DE DATA — REGRA CRÍTICA:** Ao mencionar a data de qualquer consulta, exame ou evento, copie LITERALMENTE a string da data tal como aparece no input (ex.: "2025-12-16" → "16/12/2025"). NÃO recalcule datas. NÃO infira datas a partir de queixas como "1a 2m TXF" ou "2 meses pós-Tx". NÃO ajuste datas que pareçam inconsistentes — se há divergência aparente entre data e descrição, mencione isso explicitamente em vez de corrigir silenciosamente. NÃO conte itens — use a contagem que aparecer na lista (se há 8 consultas no input, escreva "8 consultas").
1. Responda como um colega especialista, de forma técnica mas acessível
2. Cite guidelines (AASLD, EASL, ILTS, Baveno) quando relevante
3. Seja específico com doses, classificações e critérios
4. **NUNCA sugira exames que já foram realizados** - veja a lista acima
5. Use os ACHADOS DOS EXAMES DE IMAGEM para basear suas recomendações
6. Se precisar de mais informações de imagem, peça REVISÃO dos exames existentes
7. **Primeira linha para CHC avançado: Atezolizumab + Bevacizumab** (IMbrave150), NÃO sorafenib
8. Considere sempre o contexto do transplante hepático
9. Use formatação Markdown para melhor legibilidade
10. Seja conciso mas completo - respostas de 200-400 palavras são ideais
11. Se o paciente tem biópsia, use os achados histopatológicos para guiar a discussão
12. Avalie criticamente as condutas já tomadas quando relevante
13. **MELD 3.0:** O MELD deste prontuário JÁ É MELD 3.0 (incorpora sódio, albumina e sexo). NÃO recalcular como MELD-Na nem inventar scores "estimados"
14. **Plaquetopenia em cirrótico:** Trombocitopenia + esplenomegalia em paciente com hipertensão portal = hiperesplenismo por sequestro esplênico. NÃO recomendar "investigar causa" quando a causa é óbvia. NÃO recomendar "tratar plaquetopenia" como conduta isolada — tratar a doença de base. Transfusão de plaquetas APENAS pré-procedimento se indicado
15. **Caroli/dilatações biliares:** Avaliados por ColangioRM (MRCP) ou imagem abdominal com fase biliar, NUNCA por EDA. EDA avalia esôfago, estômago e duodeno
16. Usar "pendências pré-transplante" em vez de "clearance cirúrgico"`;
  };

  // Enviar mensagem
  const enviarMensagem = async () => {
    if (!inputMensagem.trim() || loading || !dadosPaciente) return;

    const novaMensagemUser: Mensagem = {
      id: Date.now().toString(),
      role: "user",
      content: inputMensagem,
      timestamp: new Date(),
    };

    setMensagens((prev) => [...prev, novaMensagemUser]);
    salvarMensagem(novaMensagemUser);
    const perguntaAtual = inputMensagem;
    setInputMensagem("");
    setLoading(true);

    try {
      // Buscar contexto dos guidelines
      let contextoGuidelines = "";
      try {
        contextoGuidelines = await obterContextoGuidelines(dadosPaciente);
      } catch (e) {
        console.warn("⚠️ RAG indisponível para chat");
      }

      // Montar histórico de mensagens para a API
      const historicoAPI = mensagens
        .filter((m) => m.id !== "inicial")
        .map((m) => ({
          role: m.role,
          content: m.content,
        }));

      // Montar prompt completo com histórico de conversa
      const historicoFormatado = historicoAPI
        .map(
          (m) =>
            `**${m.role === "user" ? "Médico" : "Assistente"}:** ${m.content}`
        )
        .join("\n\n");

      const promptCompleto = `${gerarPromptSistema(dadosPaciente, modo)}
${
  contextoGuidelines
    ? `\n## CONTEXTO DOS GUIDELINES:\n${contextoGuidelines}`
    : ""
}

## HISTÓRICO DA CONVERSA:
${historicoFormatado}

## PERGUNTA ATUAL DO MÉDICO:
${perguntaAtual}

Responda de forma detalhada e técnica:`;

      // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
      // Chat usa shape genérico do /api/analyze. Mantém suporte a duas
      // formas de resposta (content[] vs resposta) por compatibilidade
      // defensiva pré-existente — ver B138 backlog para investigar se
      // o fallback data.resposta ainda é exercitado em produção.
      const data = await callAnalyze<{
        content?: Array<{ type: string; text: string }>;
        resposta?: string;
      }>({
        prompt: promptCompleto,
        useThinking: true,
      });

      // Extrair conteúdo da resposta (formato Anthropic)
      let conteudo = "";
      if (data.content && data.content[0] && data.content[0].text) {
        conteudo = data.content[0].text;
      } else if (data.resposta) {
        conteudo = data.resposta;
      } else {
        conteudo = "Desculpe, não consegui processar sua pergunta.";
      }

      const novaMensagemAssistant: Mensagem = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: conteudo,
        timestamp: new Date(),
      };

      setMensagens((prev) => [...prev, novaMensagemAssistant]);
      salvarMensagem(novaMensagemAssistant);
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      const mensagemErro: Mensagem = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          "❌ Erro ao processar sua mensagem. Por favor, tente novamente.",
        timestamp: new Date(),
      };
      setMensagens((prev) => [...prev, mensagemErro]);
    }

    setLoading(false);
  };

  // Tecla Enter para enviar
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  // Sugestões rápidas
  const sugestoesRapidas = [
    "Quais as opções terapêuticas para este caso?",
    "O paciente é elegível para transplante?",
    "Qual o prognóstico baseado nos dados atuais?",
    "Compare os critérios de Milão vs UCSF",
    "Avalie criticamente a conduta atual",
  ];

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "90%",
          maxWidth: "800px",
          height: "85vh",
          backgroundColor: "white",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            backgroundColor: theme.primary[600],
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "600",
                fontFamily: theme.fonts.sans,
              }}
            >
              <MessageCircle size={16} style={{ marginRight: "8px" }} />{" "}
              Discussão do Caso
            </h2>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "14px",
                opacity: 0.9,
                fontFamily: theme.fonts.sans,
              }}
            >
              {paciente.nome} • {dadosPaciente?.idade || "..."} anos
              {mensagens.length > 1 && ` • ${mensagens.length} mensagens`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {mensagens.length > 1 && (
              <button
                onClick={limparHistorico}
                title="Limpar histórico"
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "white",
                  fontSize: "12px",
                  cursor: "pointer",
                  padding: "6px 10px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Trash2 size={13} /> Nova discussão
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* B330 (AI_148): seletor de modo do chat + tooltip explicativo (B330-FU).
            Wrapper position:relative (SEM overflow) para a janela de explicação
            não ser cortada pela barra rolável dos modos. */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div
            ref={modosRowRef}
            style={{
              display: "flex",
              gap: "6px",
              padding: "8px 24px",
              overflowX: "auto",
              borderBottom: `1px solid ${theme.gray[200]}`,
              backgroundColor: "#ffffff",
            }}
          >
            {MODOS_CHAT.map((m) => {
              const ativo = m.id === modo;
              return (
                <button
                  key={m.id}
                  onClick={() => setModo(m.id)}
                  disabled={loading}
                  aria-label={m.descricao}
                  onMouseEnter={(e) =>
                    setModoHover({
                      id: m.id,
                      left:
                        e.currentTarget.offsetLeft -
                        (modosRowRef.current?.scrollLeft || 0),
                    })
                  }
                  onMouseLeave={() => setModoHover(null)}
                  onFocus={(e) =>
                    setModoHover({
                      id: m.id,
                      left:
                        e.currentTarget.offsetLeft -
                        (modosRowRef.current?.scrollLeft || 0),
                    })
                  }
                  onBlur={() => setModoHover(null)}
                  style={{
                    border: ativo
                      ? `1px solid ${theme.primary[600]}`
                      : `1px solid ${theme.gray[300]}`,
                    background: ativo ? theme.primary[600] : "white",
                    color: ativo ? "white" : theme.gray[700],
                    fontSize: "12px",
                    fontWeight: ativo ? 600 : 500,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading && !ativo ? 0.5 : 1,
                    padding: "5px 12px",
                    borderRadius: "999px",
                    whiteSpace: "nowrap",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          {modoHover && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: Math.max(12, modoHover.left),
                marginTop: "6px",
                maxWidth: "260px",
                zIndex: 40,
                background: theme.gray[800],
                color: "white",
                fontSize: "12px",
                lineHeight: 1.45,
                padding: "8px 11px",
                borderRadius: "8px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.22)",
                pointerEvents: "none",
                fontFamily: theme.fonts.sans,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: "2px" }}>
                {getModoConfig(modoHover.id).label}
              </div>
              {getModoConfig(modoHover.id).descricao}
            </div>
          )}
        </div>

        {/* Chat Container */}
        <div
          ref={chatContainerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 24px",
            backgroundColor: "#f9fafb",
          }}
        >
          {carregandoDados ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                color: theme.gray[500],
              }}
            >
              <Clock size={32} style={{ marginBottom: "16px" }} />
              <p>Carregando dados do paciente...</p>
            </div>
          ) : (
            <>
              {mensagens.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    display: "flex",
                    justifyContent:
                      msg.role === "user" ? "flex-end" : "flex-start",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "80%",
                      padding: "12px 16px",
                      borderRadius:
                        msg.role === "user"
                          ? "16px 16px 4px 16px"
                          : "16px 16px 16px 4px",
                      backgroundColor:
                        msg.role === "user" ? theme.primary[600] : "white",
                      color: msg.role === "user" ? "white" : theme.gray[800],
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      fontFamily: theme.fonts.sans,
                      fontSize: "14px",
                      lineHeight: "1.6",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {/* Renderizar Markdown básico */}
                    {msg.content.split("\n").map((linha, i) => {
                      // Headers
                      if (linha.startsWith("### ")) {
                        return (
                          <h4
                            key={i}
                            style={{
                              margin: "12px 0 8px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            {linha.replace("### ", "")}
                          </h4>
                        );
                      }
                      if (linha.startsWith("## ")) {
                        return (
                          <h3
                            key={i}
                            style={{
                              margin: "12px 0 8px 0",
                              fontSize: "15px",
                              fontWeight: "600",
                            }}
                          >
                            {linha.replace("## ", "")}
                          </h3>
                        );
                      }
                      if (linha.startsWith("**") && linha.endsWith("**")) {
                        return (
                          <p
                            key={i}
                            style={{ margin: "8px 0", fontWeight: "600" }}
                          >
                            {linha.replace(/\*\*/g, "")}
                          </p>
                        );
                      }
                      // Lista
                      if (linha.startsWith("- ") || linha.startsWith("• ")) {
                        return (
                          <p key={i} style={{ margin: "4px 0 4px 16px" }}>
                            • {linha.replace(/^[-•]\s/, "")}
                          </p>
                        );
                      }
                      // Lista numerada
                      if (/^\d+\.\s/.test(linha)) {
                        return (
                          <p key={i} style={{ margin: "4px 0 4px 16px" }}>
                            {linha}
                          </p>
                        );
                      }
                      // Linha normal
                      return linha ? (
                        <p key={i} style={{ margin: "4px 0" }}>
                          {linha.replace(/\*\*(.*?)\*\*/g, "$1")}
                        </p>
                      ) : (
                        <br key={i} />
                      );
                    })}
                  </div>
                </div>
              ))}

              {loading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      borderRadius: "16px 16px 16px 4px",
                      backgroundColor: "white",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      color: theme.gray[500],
                    }}
                  >
                    <span style={{ animation: "pulse 1.5s infinite" }}>
                      🤔 Analisando...
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sugestões rápidas */}
        {mensagens.length <= 1 && !loading && (
          <div
            style={{
              padding: "8px 24px",
              borderTop: "1px solid " + theme.gray[200],
              backgroundColor: "white",
            }}
          >
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[500],
                margin: "0 0 8px 0",
              }}
            >
              <Lightbulb size={14} style={{ marginRight: "6px" }} /> Sugestões:
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {sugestoesRapidas.map((sugestao, i) => (
                <button
                  key={i}
                  onClick={() => setInputMensagem(sugestao)}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    backgroundColor: theme.gray[100],
                    border: "1px solid " + theme.gray[200],
                    borderRadius: "16px",
                    cursor: "pointer",
                    color: theme.gray[700],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {sugestao}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input — 🔐 B88: discussão com IA é ato privativo do médico
            (espelho das firestore.rules e do gate no /api/analyze).
            Demais categorias leem o histórico do chat. */}
        {ehMedico(usuarioLogado) ? (
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid " + theme.gray[200],
            backgroundColor: "white",
            display: "flex",
            gap: "12px",
          }}
        >
          <textarea
            value={inputMensagem}
            onChange={(e) => setInputMensagem(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua pergunta sobre o caso..."
            disabled={loading || carregandoDados}
            style={{
              flex: 1,
              padding: "12px 16px",
              borderRadius: "12px",
              border: "1px solid " + theme.gray[300],
              fontFamily: theme.fonts.sans,
              fontSize: "14px",
              resize: "none",
              minHeight: "48px",
              maxHeight: "120px",
            }}
            rows={1}
          />
          <button
            onClick={enviarMensagem}
            disabled={loading || !inputMensagem.trim() || carregandoDados}
            style={{
              padding: "12px 24px",
              backgroundColor:
                loading || !inputMensagem.trim()
                  ? theme.gray[300]
                  : theme.primary[600],
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                loading || !inputMensagem.trim() ? "not-allowed" : "pointer",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {loading ? (
              <>
                <Clock size={14} style={{ marginRight: "6px" }} /> Enviando...
              </>
            ) : (
              <>
                <Send size={14} style={{ marginRight: "6px" }} /> Enviar
              </>
            )}
          </button>
        </div>
        ) : (
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid " + theme.gray[200],
            backgroundColor: theme.gray[50],
            fontSize: "12px",
            color: theme.gray[500],
            fontFamily: theme.fonts.sans,
            textAlign: "center",
          }}
        >
          Somente médicos podem enviar mensagens na discussão de caso com IA
          (Res. CFM 2.454/2026). Você pode ler o histórico acima.
        </div>
        )}
      </div>
    </div>
  );
}
